import React, { useRef, useCallback } from "react";
import { Grid } from "@mui/material";

import FilterDropdownBase from "@/components/filters/FilterDropdown";
import MuiSearchInput from "@/components/muiCustom/search/MuiSearchInput";
import TagChipContainerBase from "@/components/muiCustom/chip/TagChip";

type CampaignFilterOption = {
  id: string | number;
  colorCode?: string;
  color_code?: string;
  [key: string]: any;
};

type CampaignListFiltersProps = {
  filters?: {
    channels?: CampaignFilterOption[];
    tags?: CampaignFilterOption[];
    [key: string]: any;
  };
  searchValue?: string;
  onSearchChange?: (_value: string) => void;
  channelsList?: CampaignFilterOption[];
  onFilterChange?: (
    _filterType: string,
    _value: CampaignFilterOption[]
  ) => void;
  tagsList?: CampaignFilterOption[];
  isLoading?: boolean;
};

type FilterDropdownRef = {
  handleRemoveSelectedFilter?: (_filter: CampaignFilterOption) => void;
};

const FilterDropdown = FilterDropdownBase as React.ComponentType<any>;
const TagChipContainer = TagChipContainerBase as React.ComponentType<any>;

const TAG_CHIP_STYLES = {
  height: "34px",
  fontSize: ".6rem",
  cursor: "pointer",
  margin: "3px",
  borderRadius: "var(--radius-control)",
  fontWeight: 700,
  color: "var(--palette-text-primary)",
  backgroundColor: "transparent",
  border: true,
  minWidth: 80,
};

const normalizeFilterTag = (filter: CampaignFilterOption) => ({
  id: filter.id,
  color_code: filter.colorCode ?? filter.color_code,
  ...filter,
});

function CampaignListFilters({
  filters,
  searchValue,
  onSearchChange,
  channelsList,
  onFilterChange,
  tagsList,
  isLoading,
}: CampaignListFiltersProps) {
  const filterDropdownRef = useRef<FilterDropdownRef>(null);

  const handleRemoveFilter = useCallback((filter: CampaignFilterOption) => {
    filterDropdownRef.current?.handleRemoveSelectedFilter(filter);
  }, []);

  const activeFilters = [
    ...new Map(
      [...(filters?.channels || []), ...(filters?.tags || [])].map((f) => [
        f.id,
        f,
      ])
    ).values(),
  ];

  return (
    <div className="my-4">
      <Grid container spacing={2} alignItems="center">
        <Grid size={{ xs: 12, md: 6, lg: 4 }}>
          <MuiSearchInput
            value={searchValue}
            onChange={onSearchChange}
            placeholder="Search.."
            size="small"
          />
        </Grid>
        <Grid size={{ xs: 12, md: 6, lg: "auto" }}>
          <FilterDropdown
            channelsList={channelsList}
            tagsList={tagsList}
            onApplyFilters={onFilterChange}
            filters={filters}
            ref={filterDropdownRef}
            isLoading={isLoading}
          />
        </Grid>
      </Grid>
      {activeFilters.length > 0 && (
        <div className="p-0 mt-2">
          <TagChipContainer
            tags={activeFilters.map(normalizeFilterTag)}
            expandTags={false}
            customTagStyles={TAG_CHIP_STYLES}
            showRemoveIcon
            onRemoveTag={handleRemoveFilter}
          />
        </div>
      )}
    </div>
  );
}

export default CampaignListFilters;
